import type { Vec3 } from '../../domain';
import type { InteractionEvent } from '../../interaction/types';
import type { IViewport } from '../../viewports';
import type { DragHandle, IOverlayObject } from '../types';

/**
 * 2D overlay representation of a distance measurement.
 *
 * Lifecycle:
 * 1. Created by DistanceMeasurementTool with both points at the same position
 * 2. During tool placement: `interactive = false`, renders as dashed preview line
 * 3. After `finalize()`: `interactive = true`, renders as solid line with drag handles
 * 4. Can be dragged by the user via IDraggable interface
 */
export class DistanceMeasurementOverlay implements IOverlayObject {
    readonly id: string;
    readonly zIndex = 100;
    interactive = false;
    visible = true;
    readonly clipByCamera = false;

    // World-space positions (source of truth)
    private point1World: Vec3;
    private point2World: Vec3;

    // Screen-space positions (derived, updated on camera change)
    private point1Canvas = { x: 0, y: 0 };
    private point2Canvas = { x: 0, y: 0 };

    // Visual config
    private readonly handleRadius = 5;
    private readonly handleHitRadius = 10;
    private readonly lineHitDistance = 6;
    private readonly colors = {
        preview: { line: 'rgba(255, 255, 255, 0.5)', dash: [6, 4] as number[] },
        active: { line: '#00ff88', handle: '#00ff88', handleStroke: '#ffffff' },
        hover: { line: '#44ffaa', handle: '#44ffaa' },
        label: { text: '#ffffff', bg: 'rgba(0, 0, 0, 0.7)' },
    };

    isHovered = false;
    private dragState: { handle: 'point1' | 'point2'; originalWorld: Vec3 } | null = null;

    constructor(
        point1: Vec3,
        point2: Vec3,
        private viewport: IViewport
    ) {
        this.id = `dist-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        this.point1World = [...point1] as Vec3;
        this.point2World = [...point2] as Vec3;
        this.updateCanvasPositions();
    }

    // ═══════════════════════════════════════════
    //  Public API (used by Tool)
    // ═══════════════════════════════════════════

    setPoint2(worldPos: Vec3): void {
        this.point2World = [...worldPos] as Vec3;
        this.updateCanvasPositions();
    }

    setIsHovered(value: boolean): void {
        this.isHovered = value;
    }

    /**
     * Transition from preview mode to interactive mode.
     * Called by the tool when placement is complete.
     */
    finalize(): void {
        this.interactive = true;
    }

    getDistance(): number {
        const dx = this.point2World[0] - this.point1World[0];
        const dy = this.point2World[1] - this.point1World[1];
        const dz = this.point2World[2] - this.point1World[2];
        return Math.sqrt(dx * dx + dy * dy + dz * dz);
    }

    // ═══════════════════════════════════════════
    //  IOverlayObject
    // ═══════════════════════════════════════════

    hitTest(canvasX: number, canvasY: number): boolean {
        if (!this.interactive) return false;

        // Test proximity to endpoints
        for (const p of [this.point1Canvas, this.point2Canvas]) {
            const dx = canvasX - p.x;
            const dy = canvasY - p.y;
            if (Math.sqrt(dx * dx + dy * dy) <= this.handleHitRadius) {
                return true;
            }
        }

        // Test proximity to the line
        return this.distanceToLineSegment(canvasX, canvasY) < this.lineHitDistance;
    }

    getPoint1Canvas(): { x: number; y: number } {
        return { ...this.point1Canvas };
    }
    getPoint2Canvas(): { x: number; y: number } {
        return { ...this.point2Canvas };
    }
    getPoint1World(): Vec3 {
        return [...this.point1World] as Vec3;
    }
    getPoint2World(): Vec3 {
        return [...this.point2World] as Vec3;
    }
    getHandleHitRadius(): number {
        return this.handleHitRadius;
    }

    setPoint1(worldPos: Vec3): void {
        this.point1World = [...worldPos] as Vec3;
        this.updateCanvasPositions();
    }

    onCameraChanged(_viewport: IViewport): void {
        this.updateCanvasPositions();
    }

    render(ctx: CanvasRenderingContext2D, _viewport: IViewport): void {
        this.updateCanvasPositions();

        const { point1Canvas: p1, point2Canvas: p2 } = this;
        const isPreview = !this.interactive;
        const colors = this.isHovered ? this.colors.hover : this.colors.active;

        // ── Line ──
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);

        if (isPreview) {
            ctx.strokeStyle = this.colors.preview.line;
            ctx.setLineDash(this.colors.preview.dash);
            ctx.lineWidth = 1.5;
        } else {
            ctx.strokeStyle = colors.line;
            ctx.setLineDash([]);
            ctx.lineWidth = 2;
        }
        ctx.stroke();
        ctx.setLineDash([]);

        // ── End markers (small perpendicular ticks) ──
        if (this.interactive) {
            this.renderEndTick(ctx, p1, p2, colors.line);
            this.renderEndTick(ctx, p2, p1, colors.line);
        }

        // ── Handles (only when finalized) ──
        if (this.interactive) {
            this.renderHandle(ctx, p1.x, p1.y, colors);
            this.renderHandle(ctx, p2.x, p2.y, colors);
        }

        // ── Distance label ──
        this.renderLabel(ctx, p1, p2);
    }

    // ═══════════════════════════════════════════
    //  IDraggable
    // ═══════════════════════════════════════════

    getDragHandles(): DragHandle[] {
        if (!this.interactive) return [];
        return [
            {
                id: 'point1',
                position: { ...this.point1Canvas },
                cursor: 'grab',
                hitRadius: this.handleHitRadius,
            },
            {
                id: 'point2',
                position: { ...this.point2Canvas },
                cursor: 'grab',
                hitRadius: this.handleHitRadius,
            },
        ];
    }

    onDragStart(handle: DragHandle | null, _event: InteractionEvent): boolean {
        if (!handle || !this.interactive) return false;

        const handleId = handle.id as 'point1' | 'point2';
        const original = handleId === 'point1' ? this.point1World : this.point2World;

        this.dragState = {
            handle: handleId,
            originalWorld: [...original] as Vec3,
        };

        return true;
    }

    onDrag(event: InteractionEvent): void {
        if (!this.dragState) return;

        const worldPos = event.currentPoints?.world;
        if (!worldPos) return;

        if (this.dragState.handle === 'point1') {
            this.point1World = worldPos;
        } else {
            this.point2World = worldPos;
        }

        this.updateCanvasPositions();
    }

    onDragEnd(_event: InteractionEvent): void {
        this.dragState = null;
    }

    onDragCancel(): void {
        if (!this.dragState) return;

        // Restore original position
        if (this.dragState.handle === 'point1') {
            this.point1World = this.dragState.originalWorld;
        } else {
            this.point2World = this.dragState.originalWorld;
        }

        this.updateCanvasPositions();
        this.dragState = null;
    }

    private updateCanvasPositions(): void {
        const p1 = this.viewport.worldToCanvas(this.point1World);
        const p2 = this.viewport.worldToCanvas(this.point2World);
        this.point1Canvas = { x: p1.x, y: p1.y };
        this.point2Canvas = { x: p2.x, y: p2.y };
    }

    private renderHandle(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        colors: { handle: string; handleStroke?: string }
    ): void {
        // Outer ring
        ctx.beginPath();
        ctx.arc(x, y, this.handleRadius, 0, Math.PI * 2);
        ctx.fillStyle = colors.handle;
        ctx.fill();

        if (colors.handleStroke) {
            ctx.strokeStyle = colors.handleStroke;
            ctx.lineWidth = 1.5;
            ctx.stroke();
        }
    }

    /**
     * Render a small perpendicular tick at the endpoint, like a real dimension line.
     */
    private renderEndTick(
        ctx: CanvasRenderingContext2D,
        endpoint: { x: number; y: number },
        other: { x: number; y: number },
        color: string
    ): void {
        const dx = other.x - endpoint.x;
        const dy = other.y - endpoint.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len < 1) return;

        // Perpendicular direction
        const perpX = -dy / len;
        const perpY = dx / len;
        const tickLen = 6;

        ctx.beginPath();
        ctx.moveTo(endpoint.x - perpX * tickLen, endpoint.y - perpY * tickLen);
        ctx.lineTo(endpoint.x + perpX * tickLen, endpoint.y + perpY * tickLen);
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5;
        ctx.stroke();
    }

    private renderLabel(
        ctx: CanvasRenderingContext2D,
        p1: { x: number; y: number },
        p2: { x: number; y: number }
    ): void {
        const dist = this.getDistance();
        const label = `${dist.toFixed(1)} mm`;

        const midX = (p1.x + p2.x) / 2;
        const midY = (p1.y + p2.y) / 2;

        ctx.font = "14px 'JetBrains Mono', monospace";
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        const metrics = ctx.measureText(label);
        const padX = 6;
        const padY = 4;
        const boxW = metrics.width + padX * 2;
        const boxH = 16 + padY * 2;

        // Offset label above the line
        const offsetY = -14;

        // Background pill
        const rx = midX - boxW / 2;
        const ry = midY + offsetY - boxH / 2;
        const radius = 4;

        ctx.beginPath();
        ctx.roundRect(rx, ry, boxW, boxH, radius);
        ctx.fillStyle = this.colors.label.bg;
        ctx.fill();

        // Text
        ctx.fillStyle = this.colors.label.text;
        ctx.fillText(label, midX, midY + offsetY);
    }

    /**
     * Compute distance from point (px, py) to the line segment [p1Canvas, p2Canvas].
     */
    private distanceToLineSegment(px: number, py: number): number {
        const { point1Canvas: a, point2Canvas: b } = this;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const lenSq = dx * dx + dy * dy;

        if (lenSq === 0) {
            return Math.hypot(px - a.x, py - a.y);
        }

        // Clamp projection parameter t to [0, 1]
        const t = Math.max(0, Math.min(1, ((px - a.x) * dx + (py - a.y) * dy) / lenSq));
        const projX = a.x + t * dx;
        const projY = a.y + t * dy;

        return Math.hypot(px - projX, py - projY);
    }
}
